// =======================
// src/paginas/renderPage.js
// =======================

import React, { useContext, useState } from "react";

import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Link,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";

import { Link as RouterLink, useLocation } from "react-router-dom";

import AppBarComponent from "../componentes/appBar";
import DrawerComponent from "../componentes/Drawer";
import Footer from "../componentes/Footer";
import { CondicaoContext } from "../contexto";
import {
  ajustarTamanhoFonte,
  componentesTipografiaAdmin,
  componentesTipografiaEscola,
  componentesTipografiaSre,
  tipografiaGlobal,
} from "../estilos/global";

const lightTheme = createTheme({
  typography: tipografiaGlobal,
  palette: {
    mode: "light",

    primary: {
      main: "#1976d2",
    },

    background: {
      default: "#f5f5f5",
    },
  },
});

const darkTheme = createTheme({
  typography: tipografiaGlobal,
  palette: {
    mode: "dark",

    primary: {
      main: "#90caf9",
    },

    background: {
      default: "#1e1e1e",
      paper: "#2a2a2a",
    },

    text: {
      primary: "#e0e0e0",
      secondary: "#b0b0b0",
    },
  },
});

const lightThemeAdmin = createTheme(lightTheme, {
  components: componentesTipografiaAdmin,
});

const darkThemeAdmin = createTheme(darkTheme, {
  components: componentesTipografiaAdmin,
});

const lightThemeSre = createTheme(lightTheme, {
  components: componentesTipografiaSre,
});

const darkThemeSre = createTheme(darkTheme, {
  components: componentesTipografiaSre,
});

const lightThemeEscola = createTheme(lightTheme, {
  components: componentesTipografiaEscola,
});

const darkThemeEscola = createTheme(darkTheme, {
  components: componentesTipografiaEscola,
});

export default function RenderPage({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const [menuExpandido, setMenuExpandido] = useState(false);

  const [darkMode] = useState(false);

  const { condicao, role, loading } = useContext(CondicaoContext);

  const { pathname } = useLocation();

  const telaPequena = useMediaQuery("(max-width:899px)");

  // =========================================================
  // VISUAL GLOBAL
  // =========================================================

  const visualGlobal = !condicao;

  const layoutAdmin = !condicao && role === "admin";

  const layoutSre = !condicao && role === "superintendent";

  const layoutAnalista = !condicao && role === "analyst";

  const layoutEscola = !condicao && role === "user";

  const layoutAcompanhaMenu =
    layoutAdmin || layoutSre || layoutAnalista || layoutEscola;

  const temaComAaCompleto = layoutSre || layoutAnalista || layoutEscola;

  const layoutAlturaTela =
    (layoutAdmin && pathname === "/admin/perfil") ||
    (layoutAnalista &&
      !telaPequena &&
      (pathname.startsWith("/aprovar-cadastro/") || pathname === "/analise"));

  const temaClaro = layoutEscola
    ? lightThemeEscola
    : layoutAdmin
      ? lightThemeAdmin
      : temaComAaCompleto
        ? lightThemeSre
        : lightTheme;
  const temaEscuro = layoutEscola
    ? darkThemeEscola
    : layoutAdmin
      ? darkThemeAdmin
      : temaComAaCompleto
        ? darkThemeSre
        : darkTheme;

  // =========================================================
  // BREADCRUMB DO ADMIN
  // =========================================================

  const paginasAdmin = {
    "/admin/painel": "Início",

    "/admin/visualizar/sre": "SREs",

    "/admin/visualizar/cidade": "Cidades",

    "/admin/usuarios": "Usuários",

    "/admin/perfil": "Meu perfil",
  };

  const paginaAtualAdmin = paginasAdmin[pathname];

  // =========================================================
  // MENU
  // =========================================================

  const toggleDrawer = (estadoForcado) => {
    if (visualGlobal && !telaPequena) {
      if (typeof estadoForcado === "boolean") {
        setMenuExpandido(estadoForcado);
      } else {
        setMenuExpandido((atual) => !atual);
      }

      return;
    }

    setMenuOpen((atual) => !atual);
  };

  const fecharMenuDesktop = () => {
    setMenuExpandido(false);
  };

  // =========================================================
  // DRAWER
  // =========================================================

  const drawer = !loading && (
    <DrawerComponent
      menuOpen={menuOpen}
      toggleDrawer={toggleDrawer}
      visualNovo={visualGlobal}
      expandido={menuExpandido}
      onCloseDesktop={fecharMenuDesktop}
      acompanharMenu={layoutAcompanhaMenu}
    />
  );

  // =========================================================
  // RENDERIZAÇÃO
  // =========================================================

  return (
    <ThemeProvider theme={darkMode ? temaEscuro : temaClaro}>
      <CssBaseline />

      <Box
        sx={{
          display: "flex",

          flexDirection: "column",

          minHeight: "100vh",

          // O perfil ocupa o espaço entre as barras; os formulários rolam dentro dele.
          ...(layoutAlturaTela && {
            height: { xs: "auto", md: "100dvh" },
          }),
        }}
      >
        <AppBarComponent
          toggleDrawer={toggleDrawer}
          darkMode={darkMode}
          visualNovo={visualGlobal}
          exibirUsuarioAdmin={layoutAdmin}
        />

        {visualGlobal ? (
          <Box
            sx={{
              display: "flex",

              flex: "1 1 auto",

              minHeight: 0,

              alignItems: "stretch",
            }}
          >
            {drawer}

            <Box
              component="main"
              sx={{
                flexGrow: 1,

                minWidth: 0,

                ...(layoutAlturaTela && {
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }),

                bgcolor: "#ffffff",

                px: {
                  xs: 2,
                  md: layoutAcompanhaMenu ? 3 : 4,
                },

                /*
                 * ANTES:
                 * py: { xs: 2, md: 3 }
                 *
                 * Agora reduzimos somente o espaço
                 * vertical global.
                 */
                pt: {
                  xs: 1.5,
                  md: 2,
                },

                pb: {
                  xs: 1,
                  md: 1,
                },
              }}
            >
              <Container
                maxWidth={layoutAcompanhaMenu ? false : "lg"}
                disableGutters
                sx={
                  layoutAlturaTela
                    ? {
                        display: "flex",
                        flexDirection: "column",
                        flex: "1 1 auto",
                        minHeight: 0,

                        ...(layoutAnalista &&
                          pathname === "/analise" && {
                            "& > :first-child": {
                              display: "flex",
                              flexDirection: "column",
                              flex: "1 1 auto",
                              minHeight: 0,
                            },
                          }),
                      }
                    : undefined
                }
              >
                {layoutAdmin && paginaAtualAdmin && (
                  <Breadcrumbs
                    aria-label="Caminho de navegação"
                    separator="›"
                    sx={{
                      mb: 1,

                      ...(layoutAlturaTela && { flexShrink: 0 }),

                      fontSize: ajustarTamanhoFonte("0.9rem"),

                      color: "#6b7892",
                    }}
                  >
                    {pathname !== "/admin/painel" && (
                      <Link
                        component={RouterLink}
                        to="/admin/painel"
                        underline="hover"
                        sx={{
                          color: "inherit",

                          fontSize: "inherit",
                        }}
                      >
                        Início
                      </Link>
                    )}

                    <Typography
                      sx={{
                        fontSize: "inherit",

                        color: "#0767d8",

                        fontWeight: 700,
                      }}
                    >
                      {paginaAtualAdmin}
                    </Typography>
                  </Breadcrumbs>
                )}

                {children}
              </Container>
            </Box>
          </Box>
        ) : (
          <>
            {drawer}

            <Container
              component="main"
              maxWidth="lg"
              sx={{
                mt: 2,

                mb: 2,

                flexGrow: 1,
              }}
            >
              {children}
            </Container>
          </>
        )}

        <Footer visualNovo={visualGlobal} />
      </Box>
    </ThemeProvider>
  );
}
